/**
 * offer-cart.js — connects the metaobject offer data to offer-engine.js.
 *
 * Responsibilities:
 *   1. read the JSON emitted by snippets/offer-data.liquid
 *   2. build the engine's cart contract from /cart.js plus the collection map
 *   3. run the engine on load and whenever the cart changes
 *   4. publish the result as `theme:offers:updated`
 *   5. describe the offers a shopper may apply, and what each is worth right now
 *
 * It calculates and publishes only. Amounts leave here as
 * integer paise and text leaves as translation keys plus placeholder values, so the
 *
 */

const CartOffers = (() => {
  const DATA_ELEMENT_ID = 'cart-offer-data';
  const CODES_STORAGE_KEY = 'tss_cart_offer_codes';
  /*
   * Removals live in sessionStorage, not localStorage. A code the shopper entered is
   * theirs to keep, but "I do not want this automatic discount" is a decision about
   * one visit. Persisting it would quietly cost them money on a later visit, since
   * they would have no way of knowing the cart was still holding the offer back.
   */
  const SUPPRESSED_STORAGE_KEY = 'tss_cart_offer_suppressed';
  const EVENT_UPDATED = 'theme:offers:updated';

  /*
   * Rejection reasons that mean "nothing is wrong with this offer — something already applied
   * is in the way". They are listed anyway, so the shopper can see the offer exists. Every
   * other reason is about the cart not qualifying, and those stay hidden.
   */
  const BLOCKED_BY_APPLIED = ['cannot_combine', 'not_best_offer', 'stack_limit_reached'];

  let data = null;
  let variantCollections = new Map();   // variantId -> [collection handles]
  let lastResult = null;
  let lastCart = null;                  // engine cart contract from the last run
  let lastRawCart = null;               // the /cart.js response behind it, gift lines included
  let lastCodes = [];                   // expanded codes from the last run
  let pending = null;

  /* --------------------------------------------------------------- offer data */

  const readData = () => {
    const element = document.getElementById(DATA_ELEMENT_ID);
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('[offers] offer data is not valid JSON:', error);
      return null;
    }
  };

  /**
   * Invert collectionVariants into variantId -> handles so a cart line can be
   * resolved in one lookup. /cart.js carries no collection information, which is
   * why this map has to come from Liquid.
   */
  const buildVariantCollections = (collectionVariants) => {
    const map = new Map();
    for (const [handle, variantIds] of Object.entries(collectionVariants ?? {})) {
      for (const variantId of variantIds) {
        map.set(variantId, [...(map.get(variantId) ?? []), handle]);
      }
    }
    return map;
  };

  /* ------------------------------------------------------------ cart contract */

  const fetchCart = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart.js responded ${response.status}`);
    return response.json();
  };

  /**
   * Shopify cart JSON to the engine's cart contract. Prices are already in paise.
   * `original_line_price` is used rather than `final_line_price` so the engine
   * always starts from undiscounted amounts.
   */
  const toCartContract = (shopifyCart) => {
    const lines = shopifyCart.items
      .filter((item) => !item.properties?._gift)
      .map((item) => ({
        key: item.key,
        variantId: item.variant_id,
        productId: item.product_id,
        collections: variantCollections.get(item.variant_id) ?? [],
        quantity: item.quantity,
        unitPrice: item.original_price,
        linePrice: item.original_line_price,
      }));

    return {
      currency: shopifyCart.currency,
      lines,
      subtotal: lines.reduce((sum, line) => sum + line.linePrice, 0),
      itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
    };
  };

  /* ------------------------------------------------------------------- codes */

  const readCodes = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(CODES_STORAGE_KEY) ?? '[]');
      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  };

  const writeCodes = (codes) => {
    window.localStorage.setItem(CODES_STORAGE_KEY, JSON.stringify([...new Set(codes)]));
  };

  /**
   * Hidden offers ship only a hash of their code, so a typed code is compared in
   * both forms. crypto.subtle needs a secure context, which any real storefront is.
   */
  const hashCode = async (code) => {
    if (!window.crypto?.subtle) return null;
    const bytes = new TextEncoder().encode(code.trim().toUpperCase());
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  };

  const readSuppressed = () => {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(SUPPRESSED_STORAGE_KEY) ?? '[]');
      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  };

  const writeSuppressed = (handles) => {
    window.sessionStorage.setItem(SUPPRESSED_STORAGE_KEY, JSON.stringify([...new Set(handles)]));
  };

  const expandCodes = async (codes) => {
    const expanded = [];
    for (const code of codes) {
      const trimmed = code.trim().toUpperCase();
      if (!trimmed) continue;
      expanded.push(trimmed);
      const hashed = await hashCode(trimmed);
      if (hashed) expanded.push(hashed);
    }
    return expanded;
  };

  /* --------------------------------------------------------------- evaluation */

  /**
   * Evaluate against a cart we already hold. Everything after the fetch lives here so the
   * fetching and non-fetching entry points cannot drift apart.
   *
   * Any queued recompute is cancelled first: this cart is the freshest there is, so a debounced
   * fetch behind it would only re-fetch and re-evaluate the same thing.
   */
  const evaluateWith = async (shopifyCart) => {
    window.clearTimeout(pending);

    lastRawCart = shopifyCart;
    lastCart = toCartContract(shopifyCart);
    lastCodes = await expandCodes(readCodes());

    lastResult = window.OfferEngine.evaluate(lastCart, data.offers, {
      appliedCodes: lastCodes,
      suppressedHandles: readSuppressed(),
      customerIsFirstTime: data.customer?.isFirstTime ?? null,
    });

    document.dispatchEvent(new CustomEvent(EVENT_UPDATED, {
      detail: { result: lastResult, cart: lastCart },
      bubbles: true,
    }));

    return lastResult;
  };

  const recompute = async () => {
    if (!data || !window.OfferEngine) return null;
    return evaluateWith(await fetchCart());
  };

  /**
   * Same as recompute, but for a caller that already has the cart — every cart/*.js response
   * carries it, so a fetch straight afterwards asks the server for what we were just handed.
   *
   * The test is deliberately for `item_count`, not just an `items` array. cart/add.js answers
   * with the line items it ADDED — `{ items: [...] }` for the multi-item form — which would
   * pass an items-only check and have the engine price a one-item cart. Only cart/update.js,
   * cart/change.js and cart.js return a whole cart, and only those carry item_count.
   *
   * Anything else falls back to fetching, so a response of the wrong shape costs a round trip
   * rather than producing wrong discounts.
   */
  const recomputeWith = async (shopifyCart) => {
    if (!data || !window.OfferEngine) return null;

    const isWholeCart = Array.isArray(shopifyCart?.items)
      && typeof shopifyCart.item_count === 'number';

    return isWholeCart ? evaluateWith(shopifyCart) : recompute();
  };

  /**
   * What the cart would look like with one extra offer in play: the offer's code is
   * treated as entered and any removal of it is ignored. Everything else — the codes
   * already entered, the other offers the shopper removed — stays as it is, so the
   * answer accounts for combine rules against what is currently applied.
   *
   * Synchronous on purpose: it reuses the cart and codes from the last run so the
   * coupon list can price a dozen offers without a dozen round trips.
   */
  const project = (handle) => {
    if (!data || !lastCart || !window.OfferEngine) return null;
    return window.OfferEngine.evaluate(lastCart, data.offers, {
      appliedCodes: lastCodes,
      forcedHandles: [handle],
      suppressedHandles: readSuppressed().filter((stored) => stored !== handle),
      customerIsFirstTime: data.customer?.isFirstTime ?? null,
    });
  };

  /** Coalesce bursts of cart events into one evaluation. */
  const scheduleRecompute = () => {
    window.clearTimeout(pending);
    pending = window.setTimeout(() => {
      recompute().catch((error) => console.error('[offers] recompute failed:', error));
    }, 120);
  };

  /* ------------------------------------------------------------ offer listing */

  /**
   * An offer can be listed to a shopper only if it can be named. Automatic offers
   * always can. A code offer only when its code is marked public — hidden offers
   * reach the browser as a hash alone, and printing one would give the code away.
   */
  const isListable = (offer) => offer.isActive !== false
    && (offer.mode === 'automatic' || offer.isPublic === true);

  const codeEntered = (offer) => Boolean(
    (offer.code && lastCodes.includes(String(offer.code).toUpperCase()))
    || (offer.codeHash && lastCodes.includes(String(offer.codeHash).toLowerCase())),
  );

  // A gift offer is always limited to the products the merchant nominated, so the
  // gift list standing in for a scope is accurate rather than a convenience.
  const hasScope = (offer) => Boolean(
    offer.scopeVariantIds?.length
    || offer.scopeCollectionHandles?.length
    || offer.gifts?.length,
  );

  /**
   * The bullet list on a coupon card: whatever the merchant typed into the offer's `terms`
   * field, and nothing else.
   *
   * There is deliberately no generated fallback. An offer with no terms entered shows no
   * list at all — the display layer hides both the list and the "View more" control that
   * would otherwise open an empty one.
   *
   * Each line is `{ text }` rather than `{ key }`. The display layer prints text as-is and
   * looks a key up, so a literal line cannot be mistaken for a translation key and put
   * through the translator by accident.
   */
  const buildTerms = (offer) => (offer.customTerms ?? []).map((text) => ({ text }));

  /** The single condition line under an offer's name. */
  const buildSubtitle = (offer) => {
    if (offer.minCartAmount != null) return { key: 'subtitle_above_amount', amount: offer.minCartAmount };
    if (offer.minCartQuantity != null) return { key: 'subtitle_min_items', count: offer.minCartQuantity };
    if (hasScope(offer)) return { key: 'subtitle_selected_items' };
    return { key: 'subtitle_your_cart' };
  };

  /**
   * The shape the display layer consumes. `name` is the offer's dashboard name,
   * which is what Shopflo shows the shopper too; `description` is the merchant's
   * customer-facing sentence.
   */
  const toView = (offer, { kind, amount, isApplied }) => ({
    handle: offer.handle,
    kind,
    name: offer.internalName || offer.code || offer.title,
    description: offer.title,
    subtitle: buildSubtitle(offer),
    terms: buildTerms(offer),
    amount,
    isApplied,
    // Gift offers only: whether the shopper picks, and how many options they pick from. A row
    // offers a way back into the picker only when both say there is a choice to make — a single
    // option is taken on the shopper's behalf and has no picker to return to.
    shopperChosen: offer.shopperChosen !== false,
    giftCount: offer.gifts?.length ?? 0,
  });

  /* ------------------------------------------------------------- free gifts */

  const giftConditionMet = (offer, subtotal, itemCount) => {
    if (offer.purchaseConditionType === 'cart_amount') {
      if (offer.minCartAmount != null && subtotal < offer.minCartAmount) return false;
      if (offer.maxCartAmount != null && subtotal > offer.maxCartAmount) return false;
      return true;
    }
    if (offer.purchaseConditionType === 'cart_quantity') {
      return offer.minCartQuantity == null || itemCount >= offer.minCartQuantity;
    }
    return true;
  };

  const giftUsable = (offer) => Boolean(
    offer.isActive !== false
    && offer.gifts?.length
    && giftConditionMet(offer, lastCart?.subtotal ?? 0, lastCart?.itemCount ?? 0),
  );

  /**
   * The gift offer the cart should currently honour, or null. Conditions are judged
   * on the undiscounted subtotal, matching the engine's `thresholdBase` policy.
   */
  const eligibleGiftOffer = () => (data?.giftOffers ?? []).find((offer) => (
    giftUsable(offer)
    && !readSuppressed().includes(offer.handle)
    && (offer.mode !== 'code' || codeEntered(offer))
  )) ?? null;

  /**
   * Everything the coupon list needs: what is applied now, and what the shopper
   * could apply along with what each would save.
   *
   * `chosenGiftHandle` is the gift offer the shopper has already picked a gift for.
   * Gift choices live in the display layer, because nothing about them reaches the
   * Shopify cart — Shopflo adds the gift at checkout.
   */
  const listOffers = ({ chosenGiftHandle = null } = {}) => {
    if (!data || !lastResult) return { applied: [], available: [] };

    const applied = [];
    const available = [];
    const appliedByHandle = new Map(lastResult.appliedOffers.map((entry) => [entry.handle, entry]));

    for (const offer of data.offers) {
      // An applied offer is always shown, listable or not: the shopper entered its
      // code, so naming it back to them gives nothing away. `isListable` only gates
      // what may be advertised.
      const hit = appliedByHandle.get(offer.handle);
      if (hit) {
        applied.push(toView(offer, {
          kind: 'offer', amount: hit.amount, isApplied: true,
        }));
        continue;
      }

      if (!isListable(offer)) continue;

      const projection = project(offer.handle);
      const wouldApply = projection?.appliedOffers.some((entry) => entry.handle === offer.handle);

      if (wouldApply) {
        available.push(toView(offer, {
          kind: 'offer',
          amount: Math.max(projection.totalDiscount - lastResult.totalDiscount, 0),
          isApplied: false,
        }));
        continue;
      }

      /*
       * It would not apply. That is only worth hiding when the cart is the problem — conditions
       * unmet, no matching lines, incomplete admin data. When the block comes from an offer
       * already applied, the offer itself is perfectly good and the shopper should still see it.
       *
       * Listed at amount 0, not at what it would be worth. It saves nothing while the applied
       * offer holds, so the card shows no saving line — and a zero can never be picked as the
       * cart's headline "apply this to save X" invite, which would be a promise we cannot keep.
       */
      const reason = projection?.rejected?.find((entry) => entry.handle === offer.handle)?.reason;
      if (!BLOCKED_BY_APPLIED.includes(reason)) continue;

      available.push(toView(offer, { kind: 'offer', amount: 0, isApplied: false }));
    }

    for (const offer of data.giftOffers ?? []) {
      if (!giftUsable(offer)) continue;

      const isApplied = offer.handle === chosenGiftHandle;
      if (!isApplied && !isListable(offer)) continue;

      // A gift carries no money discount: Shopflo adds the product at checkout, so
      // the cart total is unchanged either way.
      const view = toView(offer, {
        kind: 'gift', amount: 0, isApplied,
      });
      (isApplied ? applied : available).push(view);
    }

    available.sort((a, b) => b.amount - a.amount);
    return { applied, available };
  };

  /* ------------------------------------------------------------------- public */

  const findOffer = (handle) => (data?.offers ?? []).find((offer) => offer.handle === handle)
    ?? (data?.giftOffers ?? []).find((offer) => offer.handle === handle)
    ?? null;

  /** Forget the stored code that unlocked an offer, by hash when the code is hidden. */
  const forgetCodeFor = async (offer) => {
    const stored = readCodes();
    if (!stored.length) return;

    const keep = [];
    for (const code of stored) {
      const upper = code.trim().toUpperCase();
      const matchesPlain = offer.code && upper === String(offer.code).toUpperCase();
      // eslint-disable-next-line no-await-in-loop
      const matchesHash = offer.codeHash && (await hashCode(upper)) === String(offer.codeHash).toLowerCase();
      if (!matchesPlain && !matchesHash) keep.push(code);
    }
    writeCodes(keep);
  };

  const applyCode = async (code) => {
    writeCodes([...readCodes(), code]);
    return recompute();
  };

  const removeCode = async (code) => {
    const target = code.trim().toUpperCase();
    writeCodes(readCodes().filter((stored) => stored.trim().toUpperCase() !== target));
    return recompute();
  };

  const clearCodes = async () => {
    writeCodes([]);
    return recompute();
  };

  const applyOffer = async (handle) => {
    const offer = findOffer(handle);
    writeSuppressed(readSuppressed().filter((stored) => stored !== handle));
    if (offer?.mode === 'code' && offer.code) writeCodes([...readCodes(), offer.code]);
    return recompute();
  };

  /**
   * Two different removals, deliberately. Withdrawing a code is enough to drop a
   * code offer, and leaves no state that could later make a re-entered code look
   * invalid. An automatic offer has no code to withdraw, so it is suppressed.
   */
  const removeOffer = async (handle) => {
    const offer = findOffer(handle);
    if (offer?.mode === 'code') {
      await forgetCodeFor(offer);
    } else {
      writeSuppressed([...readSuppressed(), handle]);
    }
    return recompute();
  };

  /* --------------------------------------------------------------------- init */

  const init = () => {
    data = readData();
    if (!data) return;

    variantCollections = buildVariantCollections(data.collectionVariants);

    // The theme's own cart pipeline fires this after every add, change and remove.
    document.addEventListener('eurus:cart:items-changed', scheduleRecompute);
    document.addEventListener('theme:cart:updated', scheduleRecompute);

    scheduleRecompute();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return {
    recompute,
    recomputeWith,
    applyCode,
    removeCode,
    clearCodes,
    applyOffer,
    removeOffer,
    listOffers,
    eligibleGiftOffer,
    get result() { return lastResult; },
    get cart() { return lastCart; },
    get rawCart() { return lastRawCart; },
    get codes() { return readCodes(); },
    get offers() { return data?.offers ?? []; },
    // Free-gift offers are not evaluated by the engine — see offer-data.liquid.
    get giftOffers() { return data?.giftOffers ?? []; },
    EVENT_UPDATED,
  };
})();

window.CartOffers = CartOffers;
